import { z } from "zod";

// ---------------------------------------------------------------------------
// Help requests ("Behöver hjälp")
//
// A department head signals they need help, either over WhatsApp (GRA-289's
// two-way webhook) or from the web. Stored at:
//   Businesses/{businessId}/helpRequests/{id}
//
// Shared contract between GRA-289 and GRA-290: GRA-289 owns the inbound
// channels and writes these records (WhatsApp webhook, and/or a web
// mutation); GRA-290 only reads them (a Firestore trigger notifies the
// department's manager through the outbox). GRA-289's branch had not defined
// this shape yet when GRA-290 started, so it is defined here - see the
// GRA-289 and GRA-290 Linear threads.
// ---------------------------------------------------------------------------

export const HelpRequestSource = z.enum(["whatsapp", "web"]);
export type HelpRequestSource = z.infer<typeof HelpRequestSource>;

export interface HelpRequestRecord {
  id: string;
  businessId: string;
  salesTeamId: string | null;
  /** The department head (roster member id) who asked for help. */
  headMemberId: string;
  /** The specific daily task help is about, if any. */
  dailyTaskId: string | null;
  note: string | null;
  source: HelpRequestSource;
  /** YYYY-MM-DD, business timezone - denormalised at creation for the 17:00 summary's single-field lookup. */
  date: string;
  /** Set once the manager notification has been enqueued - the idempotency guard against a retried trigger. */
  notifiedAt: string | null;
  resolvedAt: string | null;
  createdAt: string | null;
}

export const CreateHelpRequestInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).nullable().optional(),
  headMemberId: z.string().min(1),
  dailyTaskId: z.string().min(1).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  source: HelpRequestSource,
});
export type CreateHelpRequestInput = z.infer<typeof CreateHelpRequestInput>;
